/**
 * Diff Engine
 *
 * Computes structural differences between two objects.
 * Produces flat list of changes with dot-notation paths.
 * No external dependencies.
 */

import { createModuleLogger } from './logger';

const logger = createModuleLogger('DiffEngine');

export type DiffEntryType = 'added' | 'removed' | 'changed';

/**
 * Represents a single difference between two values
 */
export interface DiffEntry {
  /** Dot-notation path, e.g. "body.data.metadata.source" */
  path: string;
  /** Type of change */
  type: DiffEntryType;
  /** Value before change (undefined if added) */
  before?: unknown;
  /** Value after change (undefined if removed) */
  after?: unknown;
}

/**
 * Diff computation engine
 * Static methods only - no instance state
 */
export class DiffEngine {
  /**
   * Compute all differences between two objects.
   * Returns flat list of DiffEntry for each field-level change.
   *
   * @param before Starting object (or primitive)
   * @param after Ending object (or primitive)
   * @param basePath Optional base path for nested calls
   * @returns Array of DiffEntry for all changes
   */
  static compute(before: unknown, after: unknown, basePath: string = ''): DiffEntry[] {
    const diffs: DiffEntry[] = [];

    try {
      // Primitives: compare directly
      if (!isObject(before) || !isObject(after)) {
        if (!deepEqual(before, after)) {
          diffs.push({
            path: basePath || 'root',
            type: typeof before === 'undefined' ? 'added' : typeof after === 'undefined' ? 'removed' : 'changed',
            before,
            after,
          });
        }
        return diffs;
      }

      // Both are objects: walk keys
      const beforeKeys = new Set(getAllKeys(before as Record<string, unknown>));
      const afterKeys = new Set(getAllKeys(after as Record<string, unknown>));

      // Check removed and changed keys
      for (const key of beforeKeys) {
        const beforeVal = (before as Record<string, unknown>)[key];
        const afterVal = (after as Record<string, unknown>)[key];
        const path = basePath ? `${basePath}.${key}` : key;

        if (!afterKeys.has(key)) {
          // Removed
          diffs.push({ path, type: 'removed', before: beforeVal });
        } else if (isObject(beforeVal) && isObject(afterVal)) {
          // Both objects: recurse
          diffs.push(...this.compute(beforeVal, afterVal, path));
        } else if (!deepEqual(beforeVal, afterVal)) {
          // Changed
          diffs.push({
            path,
            type: 'changed',
            before: beforeVal,
            after: afterVal,
          });
        }
      }

      // Check added keys
      for (const key of afterKeys) {
        if (!beforeKeys.has(key)) {
          const afterVal = (after as Record<string, unknown>)[key];
          const path = basePath ? `${basePath}.${key}` : key;
          diffs.push({
            path,
            type: 'added',
            after: afterVal,
          });
        }
      }
    } catch (error) {
      logger.warn(`[DiffEngine.compute] Error during diff: ${error}`);
      // On error, return empty diffs (safe fallback)
    }

    return diffs;
  }

  /**
   * Quick check if two values have any differences.
   * Returns true immediately if any difference found (no full computation).
   *
   * @param before Starting value
   * @param after Ending value
   * @returns true if there are any differences
   */
  static hasDiff(before: unknown, after: unknown): boolean {
    return !deepEqual(before, after);
  }
}

// Helper functions

/**
 * Check if value is an object (not null, not array, not primitive)
 */
function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Get all enumerable keys from an object (own + inherited)
 */
function getAllKeys(obj: Record<string, unknown>): string[] {
  const keys: string[] = [];
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      keys.push(key);
    }
  }
  return keys;
}

/**
 * Deep equality check using JSON serialization
 * Fast heuristic for primitives and small objects
 */
function deepEqual(a: unknown, b: unknown): boolean {
  // Fast path for primitives
  if (a === b) return true;
  if (typeof a !== typeof b) return false;

  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}
